<template lang="pug">
span
  input(
    v-model="repeatable.model",
    :type="repeatable.type",
    :name="repeatable.name",
    :id="repeatable.name"
  )

  ExpandGroup(@group-removed="repeatable.cleanUpModel")
    div(v-for="group in repeatable.groups", :key="group.id", :id="group.id")
      bundle
        template(
          v-for="(sub, i) in group.subs",
          :key="sub.id || sub.type + '-' + i"
        )
          slot(name="default", :is="sub.type.toLowerCase()", :sub="sub")
      button(v-if="repeatable.groups.length > repeatable.min" @click.stop.prevent="repeatable.removeGroup(group.id)")
        i.fas.fa-times-circle

  control(v-if="repeatable.groups.length < repeatable.max")
    field
      button(@click.stop.prevent="repeatable.createGroup")
        i.fas.fa-plus-circle
        | {{ repeatable.label }}

      error(v-if="!repeatable.meta.valid && repeatable.errorMessage") {{ repeatable.errorMessage }}
</template>

<script>
import { useStore } from 'vuex';
import { useField } from 'vee-validate';
import { reactive, computed, watch } from 'vue';
import { cloneDeep, remove } from 'lodash-es';
import * as yup from 'yup';

import ExpandGroup from '../../transitions/ExpandGroup.vue';

export default {
  name: 'Repeatable',
  props: {
    field: Object,
  },
  components: {
    ExpandGroup,
  },
  setup(p) {
    const store = useStore();
    const repeatable = reactive(initRepeatable());

    yup.setLocale({
      number: {
        min: 'You must provide at least ${min}',
      },
    });

    return { repeatable };

    function initRepeatable() {
      const max = p.field.max;
      const min = p.field.min;
      const initial = p.field.initial;
      const empty = p.field.empty;
      const name = p.field.id_prefix;
      const label = p.field.label || null;
      const type = 'hidden';
      const groups = reactive([]);
      const fields = p.field.fields;
      const rules =
        p.field.required === false || min === 0
          ? ''
          : yup.number().integer().min(min).required();

      const initialEmptySections = empty ? false : initial ? initial : min ? min : false;

      let groupIDs = new Set(
        Object.keys(store.getters.model)
          .filter(id => id.startsWith(name) && !id.endsWith('-section_count'))
          .map(id => {
            try {
              id = id.replace(name, '');
              id = /-(\d+?)-/.exec(id)[0];
              id = id.replaceAll('-', '');
              id = parseInt(id);
            } catch {
              id = NaN;
            }
            return id;
          })
          .filter(id => !isNaN(id))
          .sort()
      );
      groupIDs = new Set(
        groupIDs.size
          ? groupIDs
          : initialEmptySections
          ? Array(initialEmptySections)
              .fill(0)
              .map((_, i) => i)
          : null
      );

      let repeatID = groupIDs.size ? Math.max(...groupIDs) + 1 : 0;

      const { value, errorMessage, meta, validate } = useField(name, rules, {
        initialValue: repeatID,
      });

      const model = computed({
        get: () => store.getters.model[`${name}-section_count`],
        set: v => {
          value.value = v;
          store.commit('updateModel', { id: `${name}-section_count`, value: v });
        },
      });
      model.value = repeatID;

      const createGroup = (_, id = repeatID, inc = true) => {
        const groupID = `${name}-${id}`;
        const makeSubs = field => {
          const id_suffix = field.id_suffix;

          if (field.type === 'repeatable' || field.type === 'duration') {
            field.id_prefix = `${groupID}-${field.id_prefix}`;
          } else if (id_suffix) {
            field.id = `${groupID}-${id_suffix}`;
          }
          field.groupID = groupID;
          if (field.fields) {
            field.fields = field.fields.map(makeSubs);
          }
          field.groupIndex = computed(() => {
            return groups.findIndex(g => g.id === groupID);
          });
          return field;
        };

        const group = {
          id: groupID,
          subs: cloneDeep(fields).map(makeSubs),
        };

        if (inc) repeatID++;
        groups.push(group);
        store.commit('updateModel', {
          id: `${name}-section_count`,
          value: groups.length,
        });
      };

      const removeGroup = groupID => {
        let count = store.getters.model[`${name}-section_count`];
        count--;
        store.commit('updateModel', { id: `${name}-section_count`, value: count });

        remove(groups, group => group.id === groupID);
      };

      const cleanUpModel = groupID => {
        const modelIDs = Object.keys(store.getters.model).filter(id =>
          id.startsWith(groupID)
        );
        modelIDs.forEach(id => store.commit('deleteModelEntry', id));
      };

      watch(
        () => groups.length,
        () => {
          model.value = groups.length;
          validate();
        }
      );

      const containsValues = groupIDs.size ? true : false;
      if (!empty || containsValues) groupIDs.forEach(id => createGroup(null, id, false));

      return {
        name,
        label,
        type,
        groups,
        model,
        meta,
        errorMessage,
        createGroup,
        removeGroup,
        cleanUpModel,
        min,
        max,
      };
    }
  },
};
</script>

<style lang="scss" scoped>
span {
  display: contents;

  &:deep(> fieldset:empty + form-control) {
    button {
      margin-right: 0;
    }
  }
}

button {
  appearance: none;
  border: none;
  background: transparent;
  display: flex;
  align-self: flex-end;
  align-items: center;
  gap: 0.5rem;
  color: $white;
  border-radius: 4px;
  background: #adb4b9;
  font-size: 14px;
  padding: 6px 8px;
  margin: 0.5rem 2.75rem 0.5rem 0;
  outline: none;
  cursor: pointer;
  transition: 0.35s ease;
  flex: 0 0 auto;
  text-decoration: none;

  &:hover {
    background: #4e626d;
  }

  &.invalid {
    color: $red;

    &:hover {
      color: darken($red, 20%);
      border-bottom-color: darken($red, 20%);
    }
  }
}

div {
  display: flex;
  flex-direction: row;

  & > :deep(form-bundle) {
    flex: 1 1 100%;
    padding-right: 1.75rem;
    margin-right: 1.25rem;
    margin-bottom: 1.75rem;
    border-right: 3px solid #efefef;
  }

  & > button {
    top: 1rem;
    position: sticky;
    flex: 0 0 auto;
    align-self: flex-start;
    color: $red;
    margin: 1rem 0 0;
    padding: 0;
    background: none;
    text-decoration: none;
    font-weight: 700;
    font-size: 22px;

    &:hover {
      text-decoration: none;
      background: transparent;
      color: darken($red, 15%);
    }
  }
}
</style>
